import { useCallback, useEffect, useRef, useState } from 'react';
import http from 'src/services/httpService';
import { FormattedMessage, useIntl } from 'react-intl';
import LoadingContainer from '../../componentes/loading-container';
import errorHandler from '../../utils/error-handler';
import { Table } from 'reactstrap';
import Avatar from '../../componentes/avatar';
import { prepareHtml, stripHtml } from '../../utils/html';
import { format, isValid } from 'date-fns';
import formatDateFns from 'src/utils/formatDateFns';
import Pagination from 'src/componentes/list/pagination';
import Button from 'src/componentes/button';
import Tooltip from 'src/componentes/tooltip';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import { capitalizeFirstLetter } from 'src/utils/string';

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 40];
const MAX_CARACTERES_VALOR = 160;

function ValorHistorico({ valor, titulo, html = false }) {
  const modalAberto = useRef(false);
  const conteudo = String(valor ?? '');
  const texto = html ? stripHtml(conteudo) : conteudo;
  const caracteres = Array.from(texto);
  const truncado = caracteres.length > MAX_CARACTERES_VALOR;
  const renderCompleto = () =>
    html ? (
      <div
        className="tw:max-w-full tw:overflow-x-auto tw:whitespace-pre-wrap tw:wrap-anywhere"
        dangerouslySetInnerHTML={prepareHtml(conteudo)}
      />
    ) : (
      <div className="tw:whitespace-pre-wrap tw:wrap-anywhere">{texto}</div>
    );

  function abrirCompleto() {
    if (modalAberto.current) return;
    modalAberto.current = true;

    try {
      ContentManager.addContent(
        <Alert
          title={capitalizeFirstLetter(titulo)}
          ignoreResourceTitle
          textCapitalizeTitle={false}
          titleCapitalizeNone
          message={renderCompleto()}
          afterClose={() => {
            modalAberto.current = false;
          }}
        />
      );
    } catch (error) {
      modalAberto.current = false;
      errorHandler(error);
    }
  }

  if (!truncado) return renderCompleto();

  return (
    <Tooltip
      text={texto}
      textAlign="left"
      maxWidth="min(40rem, calc(100vw - 2rem))"
      className="tw:max-h-80 tw:overflow-y-auto tw:whitespace-pre-wrap tw:wrap-anywhere"
    >
      <Button
        type="button"
        color="link"
        className="tw:h-auto tw:w-full tw:justify-start tw:p-0 tw:text-left tw:font-normal tw:whitespace-normal"
        aria-haspopup="dialog"
        onClick={abrirCompleto}
      >
        <span className="tw:line-clamp-3 tw:wrap-anywhere">
          {caracteres.slice(0, MAX_CARACTERES_VALOR).join('')}…
        </span>
      </Button>
    </Tooltip>
  );
}

function Versao({
  url,
  id,
  entidadeId,
  funcionalidadeId,
  refreshToken,
  onTotalLoaded,
}) {
  const intl = useIntl();
  const [alteracoes, setAlteracoes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const onTotalLoadedRef = useRef(onTotalLoaded);
  const carregandoRef = useRef(false);
  const cargaPendenteRef = useRef(null);

  useEffect(() => {
    onTotalLoadedRef.current = onTotalLoaded;
  }, [onTotalLoaded]);

  const carregar = useCallback(
    async function carregarHistorico() {
      if (carregandoRef.current) {
        cargaPendenteRef.current = carregarHistorico;
        return;
      }
      carregandoRef.current = true;
      setIsLoading(true);

      try {
        const response = await http.post(url, {
          id,
          entidadeId,
          funcionalidadeId,
        });

        setAlteracoes(response.data.alteracoes ?? []);
        setCurrentPage(1);

        if (onTotalLoadedRef.current) {
          onTotalLoadedRef.current(response.data.QuantidadeTotal);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        carregandoRef.current = false;
        const cargaPendente = cargaPendenteRef.current;
        cargaPendenteRef.current = null;
        if (cargaPendente) cargaPendente();
        else setIsLoading(false);
      }
    },
    [url, id, entidadeId, funcionalidadeId]
  );

  useEffect(() => {
    carregar();
  }, [carregar, refreshToken]);

  const changePageSize = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const renderData = (dataHora) => {
    if (!isValid(new Date(dataHora))) {
      return intl.formatMessage({ id: 'dataNaoDefinida' });
    }

    const { locale } = intl;
    const { currentLocale } = formatDateFns(locale);

    return format(new Date(dataHora), 'Pp', { locale: currentLocale });
  };

  const renderAlteracao = (alteracao, alteracaoIndex) => {
    return (
      <div key={alteracaoIndex} className="tw:min-w-0 tw:p-2.5">
        <div className="tw:mb-1 tw:inline-flex tw:items-center">
          <Avatar
            foto={alteracao && alteracao.fotoArquivo}
            size={23}
            label={alteracao.usuario}
          ></Avatar>{' '}
          <span className="tw:ms-1">- {renderData(alteracao.dataHora)}</span>
        </div>
        <Table
          size="sm"
          bordered
          className="tw:w-full tw:table-fixed tw:[&_td]:overflow-hidden tw:[&_td]:align-top tw:[&_th]:wrap-anywhere"
        >
          <thead>
            <tr>
              <th className="tw:w-1/3">
                {<FormattedMessage id="label.campo" />}
              </th>
              <th className="tw:w-1/3">
                {<FormattedMessage id="label.valorAntigo" />}
              </th>
              <th className="tw:w-1/3">
                {<FormattedMessage id="label.valorNovo" />}
              </th>
            </tr>
          </thead>
          <tbody>
            {alteracao.alteracoesCampos.map(
              (alteracaoCampo, alteracaoCampoIndex) => (
                <tr key={alteracaoCampoIndex}>
                  <td>
                    <ValorHistorico
                      valor={alteracaoCampo.campo}
                      titulo={intl.formatMessage({ id: 'label.campo' })}
                    />
                  </td>
                  <td>
                    <ValorHistorico
                      valor={alteracaoCampo.valorAnterior}
                      titulo={intl.formatMessage({ id: 'label.valorAntigo' })}
                      html
                    />
                  </td>
                  <td>
                    <ValorHistorico
                      valor={alteracaoCampo.novoValor}
                      titulo={intl.formatMessage({ id: 'label.valorNovo' })}
                      html
                    />
                  </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
        <br></br>
      </div>
    );
  };

  const renderAlteracoes = () => {
    const startIndex = (currentPage - 1) * pageSize;

    return alteracoes
      .slice(startIndex, startIndex + pageSize)
      .map((alteracao, alteracaoIndex) =>
        renderAlteracao(alteracao, startIndex + alteracaoIndex)
      );
  };

  const renderSemAlteracoes = () => {
    return (
      <div>
        <p>{<FormattedMessage id="label.nenhumaAlteracaoRealizada" />}</p>
      </div>
    );
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      {alteracoes.length ? renderAlteracoes() : renderSemAlteracoes()}
      {alteracoes.length > DEFAULT_PAGE_SIZE && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            count={alteracoes.length}
            currentPage={currentPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            changeCurrentPage={setCurrentPage}
            changePageSize={changePageSize}
            onRefreshClick={carregar}
          />
        </div>
      )}
    </LoadingContainer>
  );
}

export default Versao;
